import { useEffect, useMemo, useState } from 'react';
import {
  AVAILABILITY_HORIZON_MONTHS,
  DAY_NAMES_LONG,
  DAY_NAMES_SHORT,
  MONTH_NAMES,
  type TherapistAvailabilitySlot,
  formatTimeRanges12h,
  getWeeklyRangesForDate,
  formatSessionWallTime,
  isoDateLocal,
  isPastSessionSlot,
  isSameDay,
  isToday,
  isWithinAvailabilityHorizon,
  monthGrid,
  weekStrip,
} from '@lighthouse/shared';
import { api } from '../../services/api';

type ViewMode = 'weekly' | 'calendar';

interface Props {
  therapistId: string;
  availability: TherapistAvailabilitySlot[];
  selectedDate: string;
  selectedSlot?: string;
  onDateChange: (date: string) => void;
  onSlotChange?: (slot: string) => void;
}

export function TherapistAvailabilityPicker({
  therapistId,
  availability,
  selectedDate,
  selectedSlot,
  onDateChange,
  onSlotChange,
}: Props) {
  const [mode, setMode] = useState<ViewMode>('weekly');
  const today = useMemo(() => new Date(), []);
  const [monthOffset, setMonthOffset] = useState(0);
  const [slots, setSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [dateMap, setDateMap] = useState<Record<string, boolean>>({});

  const calendarMonth = useMemo(() => {
    const d = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  }, [today, monthOffset]);

  const monthRange = useMemo(() => {
    const first = new Date(calendarMonth.year, calendarMonth.month, 1);
    const last = new Date(calendarMonth.year, calendarMonth.month + 1, 0);
    return { from: isoDateLocal(first), to: isoDateLocal(last) };
  }, [calendarMonth]);

  const weekDays = useMemo(() => weekStrip(today, 7), [today]);
  const selected = useMemo(() => new Date(`${selectedDate}T12:00:00`), [selectedDate]);
  const selectedRanges = getWeeklyRangesForDate(availability, selected);

  const isDateAvailable = (dateStr: string, day: Date) =>
    dateMap[dateStr] ?? getWeeklyRangesForDate(availability, day).length > 0;

  useEffect(() => {
    api
      .get<{ dates: Record<string, boolean> }>(
        `/therapists/${therapistId}/available-dates?from=${monthRange.from}&to=${monthRange.to}`
      )
      .then((res) => setDateMap(res.dates))
      .catch(() => setDateMap({}));
  }, [therapistId, monthRange.from, monthRange.to]);

  useEffect(() => {
    if (!onSlotChange) return;
    setLoadingSlots(true);
    api
      .get<{ slots: string[] }>(`/therapists/${therapistId}/slots?date=${selectedDate}`)
      .then((res) => {
        setSlots(res.slots);
        if (selectedSlot && isPastSessionSlot(selectedSlot)) onSlotChange('');
      })
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [therapistId, selectedDate, onSlotChange, selectedSlot]);

  return (
    <div className="therapist-availability">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-gray-900">Availability</h3>
          <p className="text-xs text-gray-500">
            {MONTH_NAMES[calendarMonth.month]} {calendarMonth.year}
          </p>
        </div>
        <div className="availability-toggle">
          <button
            type="button"
            className={mode === 'weekly' ? 'active' : ''}
            onClick={() => setMode('weekly')}
          >
            Weekly
          </button>
          <button
            type="button"
            className={mode === 'calendar' ? 'active' : ''}
            onClick={() => setMode('calendar')}
          >
            Calendar
          </button>
        </div>
      </div>

      {mode === 'weekly' ? (
        <>
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
            {weekDays.map((day) => {
              const dateStr = isoDateLocal(day);
              const available = isDateAvailable(dateStr, day);
              const active = isSameDay(day, selected);
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => onDateChange(dateStr)}
                  className={`availability-day ${active ? 'active' : ''} ${available ? 'has-slot' : ''}`}
                >
                  <span className="dow">{DAY_NAMES_SHORT[day.getDay()]}</span>
                  <span className="dom">{day.getDate()}</span>
                  {available && <span className="dot" />}
                </button>
              );
            })}
          </div>
          <div className="availability-detail mt-3">
            <p className="text-xs font-semibold text-gray-500">
              {isToday(selected) ? 'Today' : DAY_NAMES_LONG[selected.getDay()]}
            </p>
            <p className="text-sm font-bold text-gray-900">
              {DAY_NAMES_LONG[selected.getDay()]}, {MONTH_NAMES[selected.getMonth()]}{' '}
              {selected.getDate()}
            </p>
            <p className="text-sm text-gray-600">
              {formatTimeRanges12h(
                selectedRanges.map((r) => ({ startTime: r.startTime, endTime: r.endTime }))
              )}
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              className="btn-ghost text-sm"
              disabled={monthOffset <= 0}
              onClick={() => setMonthOffset((m) => m - 1)}
            >
              ←
            </button>
            <span className="text-sm font-semibold text-gray-800">
              {MONTH_NAMES[calendarMonth.month]} {calendarMonth.year}
            </span>
            <button
              type="button"
              className="btn-ghost text-sm"
              disabled={monthOffset >= AVAILABILITY_HORIZON_MONTHS}
              onClick={() => setMonthOffset((m) => m + 1)}
            >
              →
            </button>
          </div>
          <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-gray-400">
            {DAY_NAMES_SHORT.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {monthGrid(calendarMonth.year, calendarMonth.month).map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} />;
              const dateStr = isoDateLocal(day);
              const available = isDateAvailable(dateStr, day);
              const active = isSameDay(day, selected);
              const past = day < new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const inHorizon = isWithinAvailabilityHorizon(dateStr);
              return (
                <button
                  key={dateStr}
                  type="button"
                  disabled={past || !inHorizon}
                  onClick={() => onDateChange(dateStr)}
                  className={`calendar-day ${active ? 'active' : ''} ${available ? 'has-slot' : ''} ${past || !inHorizon ? 'past' : ''}`}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </>
      )}

      {onSlotChange && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Time slots
          </p>
          {loadingSlots ? (
            <p className="text-sm text-gray-500">Loading slots…</p>
          ) : slots.length === 0 ? (
            <p className="text-sm text-gray-500">No open slots on this day.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {slots.map((slot) => {
                const past = isPastSessionSlot(slot);
                return (
                  <button
                    key={slot}
                    type="button"
                    disabled={past}
                    onClick={() => onSlotChange(slot)}
                    className={`slot-chip ${selectedSlot === slot && !past ? 'active' : ''} ${past ? 'past' : ''}`}
                  >
                    {formatSessionWallTime(slot, { hour: '2-digit', minute: '2-digit' }, 'en-NG')}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="availability-legend mt-4">
        <span>
          <i className="dot available" /> Available
        </span>
        <span>
          <i className="dot off" /> Off
        </span>
        <span>
          <i className="today ring" /> Today
        </span>
      </div>
    </div>
  );
}
